import { useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText, FlaskConical, Upload, Download, Plus, X, Globe, EyeOff, Archive,
  Loader2, CheckCircle2, AlertTriangle, Pencil,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";

// =============================================================================
// Document Library — T-429
// =============================================================================
// 30 Sep 2026 (mig 409): "Fetch sheet" / "Fetch N missing sheets" — a row with
// a source_url and no file (the 41 seeded from the old QR register, or a link
// a manager typed) gets its PDF pulled into our storage and read by the worker.
// 30 Sep 2026 (mig 407): the crew adds SDS products from the phone; the SDS
// tab shows them first as "awaiting check" with Approve / Reject
// (app.sds_review). Only approved rows reach the HSE portal.
// One place to manage everything the company publishes: the SDS register,
// SOPs/procedures, and new versions of policies + SWMS. Anything flagged
// public flows to hse.coretelgroup.com automatically (the portal reads live
// rows, 5-minute cache) — publish is a checkbox, not a deployment.
//
// Permissions mirror the DB: SDS + SOPs are manager+; policies + SWMS
// publishing is DIRECTOR (their RLS says so, and the publish RPC re-checks).
// Supervisors get a read-only view.
// =============================================================================

const GHS_PICTOGRAMS = [
  "Explosive", "Flammable", "Oxidising", "Compressed gas", "Corrosive",
  "Acute toxicity", "Harmful / irritant", "Health hazard", "Environmental hazard",
];

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}
function extOf(name: string) {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i) : "";
}
function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

// Sign → PUT to R2 (mgr+ can write any coretel-prod key via /sign-upload).
async function uploadToLibrary(objectKey: string, f: File): Promise<void> {
  const signRes = await apiFetch("/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      bucket: "coretel-prod",
      object_key: objectKey,
      content_type: f.type || "application/octet-stream",
      expires_in_seconds: 900,
    }),
  });
  if (!signRes.ok) throw new Error(`sign_failed_${signRes.status}`);
  const { upload_url } = await signRes.json();
  const put = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": f.type || "application/octet-stream" },
    body: f,
  });
  if (!put.ok) throw new Error(`upload_failed_${put.status}`);
}

// Signed download for an internal user (RLS-checked by the worker).
async function openLibraryFile(bucket: string, objectKey: string) {
  const res = await apiFetch("/sign-download", {
    method: "POST",
    body: JSON.stringify({ bucket, object_key: objectKey, expires_in_seconds: 3600 }),
  });
  if (!res.ok) { window.alert("Couldn't sign the download."); return; }
  const { download_url } = await res.json();
  window.open(download_url, "_blank");
}

type Tab = "sds" | "sops" | "published";

export default function DocumentLibraryScreen() {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const isDirector = hasRole(user, "director");
  const [tab, setTab] = useState<Tab>("sds");

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto space-y-4">
      <div>
        <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
        <h1 className="text-2xl font-semibold tracking-tight">Document library</h1>
        <p className="text-text-muted text-[13px] mt-1">
          SDS register, SOPs and published policy/SWMS versions. Anything marked{" "}
          <Globe size={11} className="inline -mt-0.5"/> public shows on hse.coretelgroup.com within 5 minutes.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {([["sds", "SDS register"], ["sops", "SOPs & procedures"], ["published", "Policies & SWMS"]] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`text-[13px] px-3.5 py-1.5 rounded-md border transition ${tab === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "sds" && <SdsTab canManage={canManage}/>}
      {tab === "sops" && <SopsTab canManage={canManage}/>}
      {tab === "published" && <PublishedTab isDirector={isDirector}/>}
    </div>
  );
}

// =============================================================================
// Tab 1 — SDS register
// =============================================================================
type SdsRow = {
  sds_id: string; product_slug: string; product_name: string; manufacturer: string | null;
  cas_numbers: string[] | null; hazard_class: string | null; ghs_pictograms: string[] | null;
  sds_issue_date: string | null; sds_review_date: string | null; sds_file_id: string | null;
  is_active: boolean; notes: string | null;
  // mig 407 — added from the phone
  status: "pending" | "approved" | "not_found" | "rejected";
  product_size: string | null; source_url: string | null; search_note: string | null;
  added_from_project_id: string | null; label_photo_file_id: string | null; approved_at: string | null; created_at: string;
  added_from?: { project_code: string; project_name: string } | null;
  added_by_user?: { login_email: string } | null;
};
const SDS_STATUS_PILL: Record<SdsRow["status"], { label: string; tone: "warning" | "ok" | "critical" }> = {
  pending: { label: "awaiting check", tone: "warning" }, approved: { label: "approved", tone: "ok" },
  not_found: { label: "sheet to source", tone: "warning" }, rejected: { label: "rejected", tone: "critical" },
};

function SdsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<SdsRow | "new" | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["library_sds"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("sds_register")
        .select("sds_id, product_slug, product_name, manufacturer, cas_numbers, hazard_class, ghs_pictograms, sds_issue_date, sds_review_date, sds_file_id, is_active, notes, status, product_size, source_url, search_note, added_from_project_id, label_photo_file_id, approved_at, created_at, added_from:projects!sds_register_added_from_project_id_fkey(project_code, project_name), added_by_user:users!sds_register_added_by_fkey(login_email)")
        .order("product_name");
      if (error) throw error;
      // pending first — those are the ones waiting on a manager (mig 407)
      const order: Record<string, number> = { pending: 0, not_found: 1, approved: 2, rejected: 3 };
      return ((data ?? []) as SdsRow[]).sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9) || a.product_name.localeCompare(b.product_name));
    },
  });

  const { data: fileById = new Map() } = useQuery({
    queryKey: ["library_sds_files", rows.map(r => `${r.sds_file_id}/${r.label_photo_file_id}`).join(",")],
    enabled: rows.some(r => r.sds_file_id || r.label_photo_file_id),
    queryFn: async () => {
      const ids = [...rows.map(r => r.sds_file_id), ...rows.map(r => r.label_photo_file_id)].filter(Boolean) as string[];
      const { data } = await supabase.from("files").select("file_id, r2_bucket, r2_object_key, file_name").in("file_id", ids);
      return new Map((data ?? []).map((f: any) => [f.file_id, f]));
    },
  });

  const visible = rows.filter(r => showArchived || (r.is_active && r.status !== "rejected"));
  const today = new Date().toISOString().slice(0, 10);
  const pendingCount = rows.filter(r => r.status === "pending").length;
  const [reviewing, setReviewing] = useState<string | null>(null);
  // mig 409 — rows seeded from the old QR-code register (and any row where a
  // manager typed a source_url) have a link but no sheet yet: the worker
  // pulls the PDF into our storage and reads the dates/class off it.
  const fetchable = rows.filter(r => !r.sds_file_id && r.source_url && r.status !== "rejected");
  const [fetching, setFetching] = useState<string | null>(null);
  const [fetchLog, setFetchLog] = useState<string | null>(null);
  async function fetchSheet(r: SdsRow): Promise<string> {
    const res = await apiFetch("/sds/fetch-sheet", { method: "POST", body: JSON.stringify({ sds_id: r.sds_id }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j?.detail || j?.error || `failed (${res.status})`);
    return `${r.product_name}: sheet attached${j.issue_date ? `, issued ${fmtDate(j.issue_date)}` : ""}${j.hazard_class ? ` — ${j.hazard_class}` : ""}${j.is_sds === false ? " ⚠ reader doubts it's an SDS" : ""}${j.matches === false ? " ⚠ names a different product" : ""}`;
  }
  async function fetchOne(r: SdsRow) {
    setFetching(r.sds_id); setFetchLog(null);
    try { setFetchLog(await fetchSheet(r)); qc.invalidateQueries({ queryKey: ["library_sds"] }); }
    catch (e: any) { setFetchLog(`${r.product_name}: ${e?.message ?? e}`); }
    finally { setFetching(null); }
  }
  async function fetchAll() {
    if (!window.confirm(`Fetch ${fetchable.length} sheet${fetchable.length === 1 ? "" : "s"} from their links into Coretel's storage? Takes ~10 s each.`)) return;
    const lines: string[] = [];
    for (const r of fetchable) {
      setFetching(r.sds_id);
      try { lines.push(await fetchSheet(r)); } catch (e: any) { lines.push(`${r.product_name}: FAILED — ${e?.message ?? e}`); }
      setFetchLog(lines.join("\n"));
      qc.invalidateQueries({ queryKey: ["library_sds"] });
    }
    setFetching(null);
  }

  // mig 407 — approve / reject what the crew added from the phone. Approve
  // publishes it to the HSE portal; reject keeps the row for the audit.
  async function review(r: SdsRow, approve: boolean) {
    let note: string | null = null;
    if (!approve) { note = window.prompt("Why is this rejected? (kept on the record)", "Wrong product / not an SDS"); if (note === null) return; }
    else if (!r.sds_file_id) { window.alert("No SDS file on this product yet — add the sheet (Edit) before approving."); return; }
    setReviewing(r.sds_id);
    try {
      const { error } = await (supabase as any).schema("app").rpc("sds_review", { p_sds_id: r.sds_id, p_approve: approve, p_note: note });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["library_sds"] });
    } catch (e: any) { window.alert(e?.message ?? String(e)); }
    finally { setReviewing(null); }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 text-[12px] text-text-muted">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}/>
          Show archived
        </label>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && <Pill label={`${pendingCount} awaiting your check`} tone="warning"/>}
          {canManage && fetchable.length > 0 && (
            <Button variant="secondary" icon={Download} onClick={() => void fetchAll()} disabled={fetching != null}>
              {fetching ? "Fetching…" : `Fetch ${fetchable.length} missing sheet${fetchable.length === 1 ? "" : "s"}`}
            </Button>
          )}
          {canManage && <Button icon={Plus} onClick={() => setEditing("new")}>Add product</Button>}
        </div>
      </div>
      {fetchLog && <pre className="text-[11px] text-text-muted whitespace-pre-wrap bg-ink-raised border border-line rounded px-3 py-2 max-h-40 overflow-y-auto">{fetchLog}</pre>}
      <div className="text-[11px] text-text-muted">The crew adds products from the phone (site folder → SDS, or the SDS tile) — a photo of the label, Ops finds and checks the sheet, they confirm. It lands here as <b>awaiting check</b>; approve it and the HSE portal publishes it. Nothing pending is public.</div>

      {isLoading && <div className="flex justify-center py-8"><Spinner size={18}/></div>}
      {!isLoading && visible.length === 0 && (
        <EmptyState icon={FlaskConical} title="SDS register is empty"
          description="Every hazardous substance used on our sites belongs here — add the product, attach its SDS PDF, and it publishes to the portal automatically."/>
      )}

      {visible.length > 0 && (
        <Card>
          <div className="divide-y divide-line/40">
            {visible.map(r => {
              const overdue = r.sds_review_date && r.sds_review_date < today;
              const f = r.sds_file_id ? (fileById as Map<string, any>).get(r.sds_file_id) : null;
              const photo = r.label_photo_file_id ? (fileById as Map<string, any>).get(r.label_photo_file_id) : null;
              const st = SDS_STATUS_PILL[r.status] ?? SDS_STATUS_PILL.pending;
              const fromPhone = !!r.added_from_project_id || !!r.label_photo_file_id;
              return (
                <div key={r.sds_id} className={`px-4 py-3 flex items-center gap-3 flex-wrap ${r.status === "pending" ? "bg-warn/5" : ""}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-[13px] text-text flex items-center gap-2 flex-wrap">{r.product_name}{r.product_size && <span className="text-text-muted">{r.product_size}</span>}{r.status !== "approved" && <Pill label={st.label} tone={st.tone} small/>}{!r.is_active && r.status !== "rejected" && <span className="text-text-muted"> · archived</span>}</div>
                    <div className="text-[11px] text-text-muted">
                      {r.manufacturer ?? "—"}
                      {r.hazard_class && <> · {r.hazard_class}</>}
                      {(r.ghs_pictograms?.length ?? 0) > 0 && <> · {r.ghs_pictograms!.join(", ")}</>}
                    </div>
                    {fromPhone && (
                      <div className="text-[11px] text-text-muted mt-0.5">
                        From the phone{r.added_by_user?.login_email ? ` · ${r.added_by_user.login_email}` : ""}{r.added_from ? ` · ${r.added_from.project_code} ${r.added_from.project_name}` : ""} · {fmtDate(r.created_at)}
                        {r.source_url && <> · <a href={r.source_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">source</a></>}
                        {photo && <> · <button type="button" onClick={() => openLibraryFile(photo.r2_bucket, photo.r2_object_key)} className="text-accent hover:underline">label photo</button></>}
                        {r.search_note && <span className="block italic">{r.search_note}</span>}
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] font-mono ${overdue ? "text-danger" : "text-text-muted"}`}>
                    {r.sds_review_date ? `review ${fmtDate(r.sds_review_date)}${overdue ? " — OVERDUE" : ""}` : "no review date"}
                  </span>
                  {!r.sds_file_id && <Pill label="no SDS file" tone="warning" small/>}
                  {canManage && !r.sds_file_id && r.source_url && r.status !== "rejected" && (
                    <Button size="sm" variant="secondary" onClick={() => void fetchOne(r)} disabled={fetching != null}>{fetching === r.sds_id ? "Fetching…" : "Fetch sheet"}</Button>
                  )}
                  {f && (
                    <button type="button" onClick={() => openLibraryFile(f.r2_bucket, f.r2_object_key)}
                      className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"><Download size={12}/> SDS</button>
                  )}
                  {canManage && (r.status === "pending" || r.status === "not_found") && (
                    <span className="inline-flex gap-1">
                      <Button size="sm" onClick={() => review(r, true)} disabled={reviewing === r.sds_id}>Approve</Button>
                      <Button size="sm" variant="secondary" onClick={() => review(r, false)} disabled={reviewing === r.sds_id}>Reject</Button>
                    </span>
                  )}
                  {canManage && (
                    <button type="button" onClick={() => setEditing(r)}
                      className="text-text-muted hover:text-accent p-1 rounded" title="Edit"><Pencil size={13}/></button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {editing && (
        <SdsModal
          row={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["library_sds"] }); }}
        />
      )}
    </div>
  );
}

function SdsModal({ row, onClose, onSaved }: { row: SdsRow | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(row?.product_name ?? "");
  const [manufacturer, setManufacturer] = useState(row?.manufacturer ?? "");
  const [hazardClass, setHazardClass] = useState(row?.hazard_class ?? "");
  const [ghs, setGhs] = useState<string[]>(row?.ghs_pictograms ?? []);
  const [cas, setCas] = useState((row?.cas_numbers ?? []).join(", "));
  const [issue, setIssue] = useState(row?.sds_issue_date ?? "");
  const [review, setReview] = useState(row?.sds_review_date ?? "");
  const [notes, setNotes] = useState(row?.notes ?? "");
  const [active, setActive] = useState(row?.is_active ?? true);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function onIssueChange(v: string) {
    setIssue(v);
    // SDS good practice: review within 5 years of issue — prefill, editable.
    if (v && !review) {
      const d = new Date(v); d.setFullYear(d.getFullYear() + 5);
      setReview(d.toISOString().slice(0, 10));
    }
  }

  async function save() {
    if (!name.trim()) { setErr("Product name is required."); return; }
    setBusy(true); setErr(null);
    try {
      const fields = {
        product_name: name.trim(),
        manufacturer: manufacturer.trim() || null,
        hazard_class: hazardClass.trim() || null,
        ghs_pictograms: ghs.length ? ghs : null,
        cas_numbers: cas.trim() ? cas.split(",").map(s => s.trim()).filter(Boolean) : null,
        sds_issue_date: issue || null,
        sds_review_date: review || null,
        notes: notes.trim() || null,
        is_active: active,
      };

      let sdsId = row?.sds_id;
      if (!sdsId) {
        const { data, error } = await supabase.from("sds_register")
          .insert({ ...fields, product_slug: `${slugify(name)}-${Date.now().toString(36)}` })
          .select("sds_id").single();
        if (error) throw error;
        sdsId = data.sds_id;
      } else {
        const { error } = await supabase.from("sds_register").update(fields).eq("sds_id", sdsId);
        if (error) throw error;
      }

      if (file) {
        const key = `sds/${slugify(name)}_${Date.now().toString(36)}${extOf(file.name) || ".pdf"}`;
        await uploadToLibrary(key, file);
        const { data: fRow, error: fErr } = await supabase.from("files").insert({
          parent_table: "sds_register",
          parent_id: sdsId,
          r2_bucket: "coretel-prod",
          r2_object_key: key,
          file_name: file.name,
          content_type: file.type || "application/pdf",
          file_size_bytes: file.size,
        }).select("file_id").single();
        if (fErr) throw fErr;
        const { error: uErr } = await supabase.from("sds_register").update({ sds_file_id: fRow.file_id }).eq("sds_id", sdsId);
        if (uErr) throw uErr;
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={row ? "Edit SDS entry" : "Add product to the SDS register"} onClose={onClose}>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <label className="block text-[12px] text-text-muted">Product name *
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. CRC 5.56"/>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">Manufacturer
          <Input value={manufacturer} onChange={e => setManufacturer(e.target.value)}/>
        </label>
        <label className="block text-[12px] text-text-muted">Hazard class
          <Input value={hazardClass} onChange={e => setHazardClass(e.target.value)} placeholder="e.g. Flammable aerosol Cat 1"/>
        </label>
      </div>
      <div>
        <div className="text-[12px] text-text-muted mb-1.5">GHS pictograms</div>
        <div className="flex flex-wrap gap-1.5">
          {GHS_PICTOGRAMS.map(g => {
            const on = ghs.includes(g);
            return (
              <button key={g} type="button" onClick={() => setGhs(prev => on ? prev.filter(x => x !== g) : [...prev, g])}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                {g}
              </button>
            );
          })}
        </div>
      </div>
      <label className="block text-[12px] text-text-muted">CAS number(s) — comma separated
        <Input value={cas} onChange={e => setCas(e.target.value)} placeholder="e.g. 64742-49-0, 106-97-8" mono/>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">SDS issue date
          <Input type="date" value={issue} onChange={e => onIssueChange(e.target.value)}/>
        </label>
        <label className="block text-[12px] text-text-muted">Review by (5yr default)
          <Input type="date" value={review} onChange={e => setReview(e.target.value)}/>
        </label>
      </div>
      <label className="block text-[12px] text-text-muted">Notes
        <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}/>
      </label>
      <div>
        <div className="text-[12px] text-text-muted mb-1.5">{row?.sds_file_id ? "Replace SDS PDF" : "SDS PDF"}</div>
        <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={e => setFile(e.target.files?.[0] ?? null)}/>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" icon={Upload} onClick={() => fileRef.current?.click()}>
            {file ? "Change file" : "Pick file"}
          </Button>
          {file && <span className="text-[12px] text-text-muted">{file.name}</span>}
        </div>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-text">
        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}/>
        Active (shows on the public portal)
      </label>
      <div className="flex gap-2 pt-1">
        <Button onClick={save} disabled={busy} icon={busy ? Loader2 : CheckCircle2}>{busy ? "Saving…" : "Save"}</Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </ModalShell>
  );
}

// =============================================================================
// Tab 2 — SOPs & procedures (documents machinery, versioned)
// =============================================================================
type DocRow = {
  document_id: string; document_code: string; document_type: string; title: string;
  description: string | null; is_public_facing: boolean; is_active: boolean;
};

function SopsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["library_sops"],
    queryFn: async () => {
      const { data, error } = await supabase.from("documents")
        .select("document_id, document_code, document_type, title, description, is_public_facing, is_active")
        .in("document_type", ["sop", "procedure", "manual"])
        .order("document_code");
      if (error) throw error;
      return (data ?? []) as DocRow[];
    },
  });

  const { data: versionByDoc = new Map() } = useQuery({
    queryKey: ["library_sop_versions", docs.map(d => d.document_id).join(",")],
    enabled: docs.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("files")
        .select("document_id, version_label, effective_from, r2_bucket, r2_object_key, file_name")
        .in("document_id", docs.map(d => d.document_id)).eq("is_current_version", true);
      return new Map((data ?? []).map((f: any) => [f.document_id, f]));
    },
  });

  async function togglePublic(d: DocRow) {
    const { error } = await supabase.from("documents").update({ is_public_facing: !d.is_public_facing }).eq("document_id", d.document_id);
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["library_sops"] });
  }
  async function toggleArchive(d: DocRow) {
    if (d.is_active && !window.confirm(`Archive ${d.document_code}? It disappears from the portal and pickers (history kept).`)) return;
    const { error } = await supabase.from("documents").update({ is_active: !d.is_active }).eq("document_id", d.document_id);
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["library_sops"] });
  }

  const visible = docs.filter(d => showArchived || d.is_active);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 text-[12px] text-text-muted">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}/>
          Show archived
        </label>
        {canManage && <Button icon={Plus} onClick={() => setAdding(true)}>New SOP / procedure</Button>}
      </div>

      {isLoading && <div className="flex justify-center py-8"><Spinner size={18}/></div>}
      {!isLoading && visible.length === 0 && (
        <EmptyState icon={FileText} title="No SOPs yet"
          description="Upload your standard operating procedures here — versioned, with history kept. Tick 'public' on any that should show on the portal; the rest stay internal for the crews."/>
      )}

      {visible.length > 0 && (
        <Card>
          <div className="divide-y divide-line/40">
            {visible.map(d => {
              const v = (versionByDoc as Map<string, any>).get(d.document_id);
              return (
                <DocRowView key={d.document_id} d={d} v={v} canManage={canManage} directorOnly={false}
                  onTogglePublic={() => togglePublic(d)}
                  onToggleArchive={() => toggleArchive(d)}
                  onPublished={() => { qc.invalidateQueries({ queryKey: ["library_sop_versions"] }); }}
                />
              );
            })}
          </div>
        </Card>
      )}

      {adding && (
        <NewSopModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["library_sops"] }); qc.invalidateQueries({ queryKey: ["library_sop_versions"] }); }}/>
      )}
    </div>
  );
}

function NewSopModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState("SOP-");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [docType, setDocType] = useState("sop");
  const [isPublic, setIsPublic] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function save() {
    if (!code.trim() || !title.trim()) { setErr("Code and title are required."); return; }
    if (!file) { setErr("Pick the document file."); return; }
    setBusy(true); setErr(null);
    try {
      const { data: doc, error } = await supabase.from("documents").insert({
        document_code: code.trim().toUpperCase(),
        document_type: docType,
        title: title.trim(),
        description: description.trim() || null,
        is_public_facing: isPublic,
      }).select("document_id").single();
      if (error) throw error;

      const key = `sops/${slugify(code)}_v1${extOf(file.name)}`;
      await uploadToLibrary(key, file);
      const { error: rpcErr } = await supabase.schema("app").rpc("publish_document_version", {
        p_document_id: doc.document_id,
        p_object_key: key,
        p_file_name: file.name,
        p_content_type: file.type || "application/octet-stream",
        p_version_label: "v1",
      });
      if (rpcErr) throw rpcErr;
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="New SOP / procedure" onClose={onClose}>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">Document code *
          <Input value={code} onChange={e => setCode(e.target.value)} mono placeholder="SOP-RIG-001"/>
        </label>
        <label className="block text-[12px] text-text-muted">Type
          <Select value={docType} onChange={e => setDocType(e.target.value)}>
            <option value="sop">SOP</option>
            <option value="procedure">Procedure</option>
            <option value="manual">Manual</option>
          </Select>
        </label>
      </div>
      <label className="block text-[12px] text-text-muted">Title *
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Tower rescue procedure"/>
      </label>
      <label className="block text-[12px] text-text-muted">Description
        <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}/>
      </label>
      <div>
        <input ref={fileRef} type="file" hidden onChange={e => setFile(e.target.files?.[0] ?? null)}/>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" icon={Upload} onClick={() => fileRef.current?.click()}>
            {file ? "Change file" : "Pick file *"}
          </Button>
          {file && <span className="text-[12px] text-text-muted">{file.name}</span>}
        </div>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-text">
        <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)}/>
        <Globe size={12}/> Show on the public portal
      </label>
      <div className="flex gap-2 pt-1">
        <Button onClick={save} disabled={busy} icon={busy ? Loader2 : CheckCircle2}>{busy ? "Publishing…" : "Create + publish v1"}</Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </ModalShell>
  );
}

// =============================================================================
// Tab 3 — Policies & SWMS (director publishing)
// =============================================================================
function PublishedTab({ isDirector }: { isDirector: boolean }) {
  const qc = useQueryClient();

  const { data: policies = [] } = useQuery({
    queryKey: ["library_policies"],
    queryFn: async () => {
      const { data, error } = await supabase.from("policies")
        .select("policy_id, policy_code, display_name, policy_category, is_public_facing, is_active, document_id, next_review_date")
        .order("policy_code");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const { data: swms = [] } = useQuery({
    queryKey: ["library_swms"],
    queryFn: async () => {
      const { data, error } = await supabase.from("swms_library")
        .select("swms_id, swms_code, display_name, scope_of_work, is_active, document_id, is_high_risk_construction_work")
        .order("swms_code");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const docIds = [...policies.map((p: any) => p.document_id), ...swms.map((s: any) => s.document_id)].filter(Boolean);
  const { data: versionByDoc = new Map() } = useQuery({
    queryKey: ["library_pub_versions", docIds.join(",")],
    enabled: docIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("files")
        .select("document_id, version_label, effective_from, r2_bucket, r2_object_key, file_name")
        .in("document_id", docIds).eq("is_current_version", true);
      return new Map((data ?? []).map((f: any) => [f.document_id, f]));
    },
  });

  async function togglePolicy(p: any, field: "is_public_facing" | "is_active") {
    const { error } = await supabase.from("policies").update({ [field]: !p[field] }).eq("policy_id", p.policy_id);
    if (error) { window.alert(`${error.message} (policies are director-managed)`); return; }
    qc.invalidateQueries({ queryKey: ["library_policies"] });
  }
  async function toggleSwms(s: any) {
    const { error } = await supabase.from("swms_library").update({ is_active: !s.is_active }).eq("swms_id", s.swms_id);
    if (error) { window.alert(`${error.message} (SWMS are director-managed)`); return; }
    qc.invalidateQueries({ queryKey: ["library_swms"] });
  }

  const refreshVersions = () => qc.invalidateQueries({ queryKey: ["library_pub_versions"] });

  return (
    <div className="space-y-5">
      {!isDirector && (
        <div className="bg-ink-raised/60 border border-line rounded-md px-3 py-2 text-[12px] text-text-muted inline-flex items-center gap-2">
          <AlertTriangle size={13} className="text-warn"/> Publishing policies and SWMS is director-only — you're viewing.
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Policies · {policies.filter((p: any) => p.is_public_facing).length} public of {policies.length}</div>
        <Card>
          <div className="divide-y divide-line/40">
            {policies.map((p: any) => {
              const v = (versionByDoc as Map<string, any>).get(p.document_id);
              return (
                <PubRow key={p.policy_id}
                  code={p.policy_code} title={p.display_name}
                  sub={`${p.policy_category ?? ""}${p.next_review_date ? ` · review ${fmtDate(p.next_review_date)}` : ""}`}
                  v={v} documentId={p.document_id}
                  isPublic={p.is_public_facing} isActive={p.is_active}
                  canEdit={isDirector}
                  uploadPrefix={`policies/${p.policy_code}`}
                  onTogglePublic={() => togglePolicy(p, "is_public_facing")}
                  onToggleActive={() => togglePolicy(p, "is_active")}
                  onPublished={refreshVersions}
                />
              );
            })}
          </div>
        </Card>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">SWMS · {swms.filter((s: any) => s.is_active).length} active of {swms.length} (inactive = hidden from the portal, e.g. the migration stubs)</div>
        <Card>
          <div className="divide-y divide-line/40">
            {swms.map((s: any) => {
              const v = (versionByDoc as Map<string, any>).get(s.document_id);
              return (
                <PubRow key={s.swms_id}
                  code={s.swms_code} title={s.display_name}
                  sub={s.scope_of_work?.slice(0, 90) ?? ""}
                  v={v} documentId={s.document_id}
                  isPublic={s.is_active} isActive={s.is_active}
                  publicLabel="active"
                  canEdit={isDirector}
                  uploadPrefix={`swms/${s.swms_code}`}
                  onTogglePublic={() => toggleSwms(s)}
                  onToggleActive={() => toggleSwms(s)}
                  onPublished={refreshVersions}
                />
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// Shared row + modal shells
// =============================================================================
function DocRowView({ d, v, canManage, onTogglePublic, onToggleArchive, onPublished }: {
  d: DocRow; v: any; canManage: boolean; directorOnly: boolean;
  onTogglePublic: () => void; onToggleArchive: () => void; onPublished: () => void;
}) {
  return (
    <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
      <span className="font-mono text-[11px] text-accent w-[110px]">{d.document_code}</span>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[13px] text-text">{d.title}{!d.is_active && <span className="text-text-muted"> · archived</span>}</div>
        <div className="text-[11px] text-text-muted">{d.document_type.toUpperCase()}{d.description ? ` · ${d.description}` : ""}</div>
      </div>
      <span className="text-[11px] font-mono text-text-muted">{v ? `${v.version_label} · ${fmtDate(v.effective_from)}` : "no file yet"}</span>
      {d.is_public_facing
        ? <Pill label="public" tone="ok" small/>
        : <Pill label="internal" tone="default" small/>}
      {v && (
        <button type="button" onClick={() => openLibraryFile(v.r2_bucket, v.r2_object_key)}
          className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"><Download size={12}/> Open</button>
      )}
      {canManage && (
        <>
          <VersionUploader documentId={d.document_id} uploadPrefix={`sops/${slugify(d.document_code)}`} onPublished={onPublished}/>
          <button type="button" onClick={onTogglePublic} title={d.is_public_facing ? "Make internal-only" : "Publish to the portal"}
            className="text-text-muted hover:text-accent p-1 rounded">{d.is_public_facing ? <EyeOff size={13}/> : <Globe size={13}/>}</button>
          <button type="button" onClick={onToggleArchive} title={d.is_active ? "Archive" : "Restore"}
            className="text-text-muted hover:text-danger p-1 rounded"><Archive size={13}/></button>
        </>
      )}
    </div>
  );
}

function PubRow({ code, title, sub, v, documentId, isPublic, isActive, canEdit, uploadPrefix, onTogglePublic, onPublished, publicLabel = "public" }: {
  code: string; title: string; sub: string; v: any; documentId: string | null;
  isPublic: boolean; isActive: boolean; canEdit: boolean; uploadPrefix: string;
  onTogglePublic: () => void; onToggleActive: () => void; onPublished: () => void;
  publicLabel?: string;
}) {
  return (
    <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
      <span className="font-mono text-[11px] text-accent w-[110px]">{code}</span>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[13px] text-text">{title}</div>
        {sub && <div className="text-[11px] text-text-muted truncate max-w-[420px]">{sub}</div>}
      </div>
      <span className="text-[11px] font-mono text-text-muted">{v ? `${v.version_label} · ${fmtDate(v.effective_from)}` : "no file"}</span>
      {isPublic ? <Pill label={publicLabel} tone="ok" small/> : <Pill label={isActive ? "hidden" : "inactive"} tone="default" small/>}
      {v && (
        <button type="button" onClick={() => openLibraryFile(v.r2_bucket, v.r2_object_key)}
          className="text-[12px] text-accent hover:underline inline-flex items-center gap-1"><Download size={12}/> Open</button>
      )}
      {canEdit && documentId && (
        <VersionUploader documentId={documentId} uploadPrefix={uploadPrefix} onPublished={onPublished}/>
      )}
      {canEdit && (
        <button type="button" onClick={onTogglePublic} title={isPublic ? "Hide from the portal" : "Show on the portal"}
          className="text-text-muted hover:text-accent p-1 rounded">{isPublic ? <EyeOff size={13}/> : <Globe size={13}/>}</button>
      )}
    </div>
  );
}

// Small inline "upload new version" control — file pick + auto version bump
// via the atomic publish RPC (mig 245).
function VersionUploader({ documentId, uploadPrefix, onPublished }: {
  documentId: string; uploadPrefix: string; onPublished: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function handle(f: File | null) {
    if (!f) return;
    const label = window.prompt("Version label for this upload (e.g. v2, v2.1):", "");
    if (label === null) return;
    setBusy(true);
    try {
      const key = `${uploadPrefix}_${Date.now().toString(36)}${extOf(f.name)}`;
      await uploadToLibrary(key, f);
      const { error } = await supabase.schema("app").rpc("publish_document_version", {
        p_document_id: documentId,
        p_object_key: key,
        p_file_name: f.name,
        p_content_type: f.type || "application/octet-stream",
        p_version_label: label.trim(),
      });
      if (error) throw error;
      onPublished();
    } catch (e: any) {
      window.alert(`Version publish failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <>
      <input ref={fileRef} type="file" hidden onChange={e => handle(e.target.files?.[0] ?? null)}/>
      <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
        title="Upload a new version (old versions kept for audit)"
        className="text-text-muted hover:text-accent p-1 rounded disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
      </button>
    </>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[8vh]">
      <div className="bg-ink-raised border border-line rounded-lg w-full max-w-[560px]">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">{title}</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-1 rounded" aria-label="Close"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}
